import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { At, Envelope } from 'phosphor-react';
import { useAppSelector, useAppDispatch } from '../../../../redux/store';
import { getSupplierInvoiceNoticeLogs } from '../../../../redux/services/invoiceNoticeLogService';
import { InvoiceNoticeLog } from '../../../../utils/interfaces/invoiceNoticeLogInterfaces';
import { DillTable, DillColumn } from '../../../general/Dill/DillTable';
import SupplierMailFaxDeliveries from './SupplierMailFaxDeliveries';

type DeliveryTab = 'EMAIL' | 'MAIL_FAX';

const DELIVERY_TABS: { id: DeliveryTab; label: string; icon: React.ReactNode }[] = [
  { id: 'EMAIL', label: 'Email', icon: <At size={14} /> },
  { id: 'MAIL_FAX', label: 'Mail & Fax', icon: <Envelope size={14} /> },
];

const toMs = (sentAt: InvoiceNoticeLog['sentAt']): number => {
  if (!sentAt) return 0;
  if (typeof sentAt === 'object' && '_seconds' in sentAt) return sentAt._seconds * 1000;
  return new Date(sentAt as string).getTime();
};

function RecipientEmailsCell({ emails }: { emails: string[] }) {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    arrowTop: 0,
    side: 'right' as 'right' | 'left',
  });
  const triggerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHide = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    clearHide();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const TOOLTIP_W = 280;
      const isLeft = rect.right + 2 + TOOLTIP_W > window.innerWidth;
      const left = isLeft ? rect.left - TOOLTIP_W - 2 : rect.right + 2;
      const estimatedH = emails.length * 52 + 56;
      const rawTop = rect.top;
      const top = Math.max(8, Math.min(rawTop, window.innerHeight - estimatedH - 8));
      setPosition({
        top,
        left,
        arrowTop: rect.top - top + rect.height / 2,
        side: isLeft ? 'left' : 'right',
      });
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    clearHide();
    timeoutRef.current = setTimeout(() => setIsHovered(false), 150);
  };

  useEffect(() => () => clearHide(), []);

  if (!emails.length) return <span className="text-gray-300 text-xs">—</span>;

  const visible = emails.slice(0, 2);
  const overflow = emails.length - 2;

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="flex gap-1 cursor-pointer w-full min-w-0">
        {visible.map((email, i) => (
          <span key={i} className="text-xs text-gray-700 truncate">
            {email}
          </span>
        ))}
        {overflow > 0 && (
          <span className="text-[10px] bg-gray-100 text-gray-500 rounded-full px-1.5 font-medium self-start">
            +{overflow} more
          </span>
        )}
      </div>
      {isHovered &&
        createPortal(
          <div
            className="fixed bg-white border border-gray-100 shadow-2xl rounded-2xl py-3 px-3 z-[99999] w-[280px] overflow-y-auto"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              maxHeight: `calc(100vh - ${position.top}px - 8px)`,
            }}
            onMouseEnter={clearHide}
            onMouseLeave={handleMouseLeave}
            onClick={(e) => e.stopPropagation()}>
            <div
              className="absolute w-3 h-3 bg-white border-gray-100"
              style={{
                top: position.arrowTop - 6,
                ...(position.side === 'right'
                  ? {
                      left: -6,
                      borderLeftWidth: 1,
                      borderBottomWidth: 1,
                      transform: 'rotate(45deg)',
                    }
                  : {
                      right: -6,
                      borderRightWidth: 1,
                      borderTopWidth: 1,
                      transform: 'rotate(45deg)',
                    }),
              }}
            />
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2.5 px-1">
              {emails.length} recipient{emails.length !== 1 ? 's' : ''}
            </p>
            <div className="flex flex-col gap-2">
              {emails.map((email, i) => (
                <div key={i} className="bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5">
                  <p className="text-xs font-medium text-gray-700 break-all leading-tight">
                    {email}
                  </p>
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

const columns: DillColumn<InvoiceNoticeLog>[] = [
  {
    key: 'buyerName',
    label: 'Buyer Name',
    sortable: true,
    filterable: true,
    getSortValue: (row) => row.buyerName ?? '',
    getSearchValue: (row) => row.buyerName ?? '',
  },
  {
    key: 'invoiceId',
    label: 'Invoice ID',
    sortable: true,
    getSortValue: (row) => row.invoiceId,
    getSearchValue: (row) => row.invoiceId,
  },
  {
    key: 'recipientEmails',
    label: 'Recipient Emails',
    getSearchValue: (row) => (row.recipientEmails ?? []).join(' '),
    render: (_, row) => <RecipientEmailsCell emails={row.recipientEmails ?? []} />,
  },
  {
    key: 'sentAt',
    label: 'Sent At',
    sortable: true,
    filterable: true,
    filterType: 'dateRange',
    getSortValue: (row) => toMs(row.sentAt),
    render: (_, row) => {
      const ms = toMs(row.sentAt);
      return ms ? new Date(ms).toLocaleString() : '—';
    },
  },
];

function SupplierInvoiceDelivery() {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { invoiceNoticeLogs, loadingList } = useAppSelector((state) => state.invoiceNoticeLogs);
  const [activeTab, setActiveTab] = useState<DeliveryTab>('EMAIL');

  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(getSupplierInvoiceNoticeLogs({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, dispatch]);

  return (
    <div className="w-full h-full p-4 flex flex-col">
      <div className="flex gap-1 p-1 rounded-xl bg-gray-200 w-fit mb-3">
        {DELIVERY_TABS.filter(
          (tab) => tab.id !== 'MAIL_FAX' || currentSupplier?.isMailAndFaxDeliveryEnabled === true
        ).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={twMerge(
              'flex items-center justify-center gap-1.5 h-[34px] min-w-[180px] px-5 text-xs font-medium rounded-lg border-none cursor-pointer transition-all',
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'bg-transparent text-gray-500 hover:text-gray-800'
            )}>
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>
      <div className="flex-1 min-h-0">
        {activeTab === 'EMAIL' && (
          <DillTable<InvoiceNoticeLog>
            columns={columns}
            data={invoiceNoticeLogs}
            rowKey={(row) => row.id}
            loading={loadingList.includes('getSupplierInvoiceNoticeLogs')}
            search
            searchPlaceholder="Search by buyer, invoice, or email..."
            defaultSort={{ key: 'sentAt', dir: 'desc' }}
            emptyMessage="No delivery notices found."
            scrollX
          />
        )}
        {activeTab === 'MAIL_FAX' &&
          currentSupplier?.id &&
          currentSupplier.isMailAndFaxDeliveryEnabled === true && (
            <SupplierMailFaxDeliveries supplierId={currentSupplier.id} />
          )}
      </div>
    </div>
  );
}

export default SupplierInvoiceDelivery;
